import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Menu, 
  MenuItem, 
  IconButton, 
  Tooltip,
  Typography,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Language as LanguageIcon,
  Check as CheckIcon
} from '@mui/icons-material';

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const current = i18n.language || 'en';
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    try {
      localStorage.setItem('appLanguage', lang);
    } catch (_) {}
    handleClose();
  };

  const languages = [
    { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
    { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
    { code: 'ms', name: 'Malay', nativeName: 'Bahasa Malaysia', flag: '🇲🇾' },
  ];

  const currentLanguage = languages.find(lang => lang.code === current) || languages[0];

  return (
    <>
      <Tooltip title="Language / 语言 / Bahasa">
        <IconButton
          onClick={handleClick}
          sx={{
            color: '#2c3e50',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderRadius: 2,
            px: 1.5,
            py: 0.75,
            minWidth: 100,
            '&:hover': {
              backgroundColor: 'rgba(52, 152, 219, 0.1)',
              borderColor: '#3498db',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(52, 152, 219, 0.2)'
            },
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <LanguageIcon sx={{ fontSize: 18, color: '#3498db' }} />
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 600, 
              color: '#2c3e50',
              fontSize: '0.875rem',
              textTransform: 'none'
            }}
          >
            {currentLanguage.nativeName}
          </Typography>
        </IconButton>
      </Tooltip>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 200,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)',
            borderRadius: 2,
            border: '1px solid rgba(0,0,0,0.06)',
            overflow: 'hidden',
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            selected={current === lang.code}
            sx={{
              backgroundColor: current === lang.code ? 'rgba(52, 152, 219, 0.08)' : 'transparent',
              '&:hover': {
                backgroundColor: current === lang.code 
                  ? 'rgba(52, 152, 219, 0.12)' 
                  : 'rgba(0, 0, 0, 0.04)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Box sx={{ fontSize: 24 }}>{lang.flag}</Box>
            </ListItemIcon>
            <ListItemText 
              primary={
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: current === lang.code ? 600 : 500,
                    color: '#2c3e50'
                  }}
                >
                  {lang.nativeName}
                </Typography>
              }
              secondary={
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#6c757d',
                    fontSize: '0.75rem'
                  }}
                >
                  {lang.name}
                </Typography>
              }
            />
            {current === lang.code && (
              <CheckIcon 
                sx={{ 
                  fontSize: 18, 
                  color: '#3498db',
                  ml: 1
                }} 
              />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSwitcher;


